import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DiagramBuilder } from '../components/DiagramBuilder/DiagramBuilder';
import { spacesService } from '../services/spaces.service';
import diagramsService from '../services/diagrams.service';
import { useSecurityAssistant } from '../contexts/SecurityAssistantContext';
import type {
  DiagramData,
  Diagram,
} from '../../../shared/src/types/space.types';

export const DiagramPage: React.FC = () => {
  const { spaceId, diagramId } = useParams<{
    spaceId: string;
    diagramId: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { setDiagramContext, clearContext } = useSecurityAssistant();

  // Fetch space
  const { data: space, isLoading: spaceLoading } = useQuery({
    queryKey: ['spaces', spaceId],
    queryFn: () => spacesService.getById(spaceId || ''),
    enabled: !!spaceId,
  });

  // Fetch or create diagram data
  const { data: diagramData } = useQuery<Diagram>({
    queryKey: ['diagram', spaceId, diagramId],
    queryFn: async () => {
      if (diagramId && !diagramId.startsWith('new-')) {
        // Load existing diagram from API
        try {
          return await diagramsService.getOne(diagramId);
        } catch {
          // Fallback to empty diagram if not found
          return {
            id: diagramId || 'unknown',
            name: 'Untitled Diagram',
            description: '',
            spaceId: spaceId || '',
            isGlobal: false,
            diagramData: {
              nodes: [],
              edges: [],
              canvasState: { zoom: 1, offsetX: 0, offsetY: 0 },
            },
            createdByUserId: '',
            createdAt: new Date(),
            updatedAt: new Date(),
          };
        }
      } else {
        // New diagram - return empty structure
        return {
          id: diagramId || 'new-diagram',
          name: 'New Diagram',
          description: '',
          spaceId: spaceId || '',
          isGlobal: false,
          diagramData: {
            nodes: [],
            edges: [],
            canvasState: { zoom: 1, offsetX: 0, offsetY: 0 },
          },
          createdByUserId: '',
          createdAt: new Date(),
          updatedAt: new Date(),
        };
      }
    },
    enabled: !!diagramId,
  });

  // Save diagram mutation
  const saveDiagramMutation = useMutation({
    mutationFn: async ({
      data,
      thumbnail,
    }: {
      data: DiagramData;
      thumbnail: string;
    }) => {
      if (!diagramId) return;

      // Update diagram data
      await diagramsService.update(diagramId, {
        diagramData: data,
      });

      // Update thumbnail if provided
      if (thumbnail) {
        await diagramsService.updateThumbnail(diagramId, thumbnail);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagrams', spaceId] });
      // Show success message
      console.info(t('diagram.saved'));
    },
  });

  // Set Security Assistant context when diagram is loaded
  useEffect(() => {
    if (diagramData && spaceId) {
      const nodes = diagramData.diagramData?.nodes || [];
      const edges = diagramData.diagramData?.edges || [];
      const componentIds = nodes
        .filter(n => n.componentId)
        .map(n => n.componentId)
        .filter(Boolean);

      setDiagramContext({
        spaceId,
        diagramId: diagramData.id,
        diagramName: diagramData.name,
        componentIds,
        componentCount: componentIds.length,
        relationCount: edges.length,
      });
    }
    // Clear context when leaving the page
    return () => {
      clearContext();
    };
  }, [diagramData, spaceId, setDiagramContext, clearContext]);

  const handleSave = async (data: DiagramData, thumbnail: string) => {
    await saveDiagramMutation.mutateAsync({ data, thumbnail });
  };

  const handleBack = () => {
    navigate(`/spaces/${spaceId}`);
  };

  if (spaceLoading || !diagramData) {
    return (
      <Container
        maxWidth="lg"
        sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (!space) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h5">{t('space.notFound')}</Typography>
        <IconButton onClick={() => navigate('/spaces')} sx={{ mt: 2 }}>
          <ArrowBackIcon />
        </IconButton>
      </Container>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top Navigation Bar */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          py: 1,
          px: 2,
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton size="small" onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <Breadcrumbs>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/spaces')}
              sx={{ textDecoration: 'none', cursor: 'pointer' }}
            >
              {t('nav.spaces')}
            </Link>
            <Link
              component="button"
              variant="body2"
              onClick={handleBack}
              sx={{ textDecoration: 'none', cursor: 'pointer' }}
            >
              {space.name}
            </Link>
            <Typography variant="body2" color="text.primary">
              {diagramData.name}
            </Typography>
          </Breadcrumbs>
        </Box>
      </Box>

      {/* Diagram Builder */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <DiagramBuilder
          spaceId={spaceId || ''}
          diagramId={diagramId}
          initialData={diagramData}
          onSave={handleSave}
        />
      </Box>
    </Box>
  );
};
